import dayjs from 'dayjs';
import groupBy from 'lodash.groupby';

import {
  ShiftReport,
  Tasks,
} from 'business/shift-report/report/pages/ReportEdit/types';

const TaskType = {
  Excavation: 'excavation',
  Build: 'build',
} as const;

const isExcavationTask = (task: Tasks[number]) => {
  return task.activity.type === TaskType.Excavation;
};

const isBuildTask = (task: Tasks[number]) => {
  return task.activity.type === TaskType.Build;
};

// Rules:
// - Group tasks by ring
// - Keep the last task of each type (excavation or build) of each ring for the current report
// - Only add remaining tasks to marked tasks if:
//   1) It's not the last task of the shift report
//   2) Only add a build task if there is no excavation on the same ring number after it
export const excavationAndBuildTasksWithoutEnd = (report: ShiftReport) => {
  if (!report || !report.tasks) {
    return null;
  }

  // Group tasks by ring
  // Note: uses lodash.groupby instead of the native Object.groupBy, which is
  // not supported on Safari < 17.4 (see CAP bug report about crashing shift report page)
  const lastTasksByRing: Tasks = [];
  const tasksByRing = groupBy(report.tasks, (task) => task.ring);

  // Keep the last tasks of each ring:
  // - If there is an excavation task, keep it
  // - If there is a build task, keep it only if there is no excavation on the same ring number after it
  Object.values(tasksByRing).forEach((tasks) => {
    const lastBuildTask = tasks?.toReversed().find((task) => isBuildTask(task));
    const lastExcavationTask = tasks
      ?.toReversed()
      .find((task) => isExcavationTask(task));

    if (lastExcavationTask) {
      lastTasksByRing.push(lastExcavationTask);

      if (
        lastBuildTask &&
        dayjs(lastBuildTask.endDate).isAfter(dayjs(lastExcavationTask.endDate))
      ) {
        lastTasksByRing.push(lastBuildTask);
      }
    }
  });

  // Filter already ended tasks and remove the last task of the report
  const markedTasks: Tasks = [];
  lastTasksByRing.forEach((task) => {
    if (task.ended) {
      return;
    }

    markedTasks.push(task);
  });
  markedTasks.pop();

  return markedTasks.length ? markedTasks : null;
};
